import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
// Common Components
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppToaster from '../../../components/AppToaster';
// API
import { getReportParameters, generateReport } from '../../../api/ReportsAPI';
import { getParties } from '../../../api/PartiesAPI';
import { getProducts } from '../../../api/ProductsAPI';
import { getHoas } from '../../../api/HoasAPI';
import { handleApiError } from '../../../api/risapi';
import { capitalizeFirstLetter } from '../../../commonfunctions';
import { format } from 'date-fns';

const GenericReportsPage = ({ moduleTitle, module }) => {
    // --- State ---
    const [config, setConfig] = useState(null);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [errors, setErrors] = useState({});

    // --- Selections ---
    const [selectedReport, setSelectedReport] = useState(null);
    const [reportType, setReportType] = useState('abstract');
    const [reportFormat, setReportFormat] = useState('pdf');

    // --- Dynamic Filters State ---
    const [filters, setFilters] = useState({
        from_date: new Date(),
        to_date: new Date(),
        party_id: null,
        product_id: null,
        hoa_id: null,
    });

    // --- Master Data for Dropdowns ---
    const [partyOptions, setPartyOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [hoaOptions, setHoaOptions] = useState([]);

    // --- 1. Load Report Configuration & Masters ---
    useEffect(() => {
        let isMounted = true;
        setLoadingConfig(true);
        
        getReportParameters(module, (res) => {
            if (!isMounted) return;
            if (res.currentPageControl) {
                setConfig(res.currentPageControl);
                const firstReport = Object.keys(res.currentPageControl)[0];
                if (firstReport) setSelectedReport(firstReport);

                if (res.filters) {
                    if (res.filters.party) {
                        setPartyOptions(res.filters.party.map(p => ({ value: p.id, label: `${p.name} ${p.code ? `(${p.code})` : ''}` })));
                    }
                    if (res.filters.product) {
                        setProductOptions(res.filters.product.map(p => ({ value: p.id, label: `${p.name} (${p.code})` })));
                    }
                    if (res.filters.hoa) {
                        setHoaOptions(res.filters.hoa.map(h => ({ value: h.id, label: `${h.name} (${h.code})` })));
                    }
                }
            }
            setLoadingConfig(false);
        });

        return () => { isMounted = false; };
    }, [module]);

    // --- 2. Auto-select first valid Report Type (Abstract/Detail) ---
    useEffect(() => {
        if (!config || !selectedReport) return;
        const availableTypes = config[selectedReport];
        if (!availableTypes[reportType]) {
            const firstValidType = Object.keys(availableTypes)[0];
            if (firstValidType) setReportType(firstValidType);
        }
    }, [selectedReport, config, reportType]);

    // --- Helpers ---
    const getActiveFilters = () => {
        if (!config || !selectedReport) return [];
        return config[selectedReport]?.[reportType]?.filters || [];
    };
    const hasFilter = (filterName) => getActiveFilters().includes(filterName);

    // --- Handlers ---
    const handleGenerate = () => {
        setGenerating(true);
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
        setErrors({});

        const payload = {
            selectedOption: selectedReport,
            selectedType: reportType,
            reportFormat: reportFormat,
            from_date: format(filters.from_date, 'yyyy-MM-dd'),
            to_date: format(filters.to_date, 'yyyy-MM-dd'),
            party_id: filters.party_id,
            product_id: filters.product_id,
            hoa_id: filters.hoa_id,
        };

        generateReport(module, payload, (response) => {
            // ** FIX IS HERE: Stop loading immediately, no matter what **
            setGenerating(false);

            // Handle validation error (e.g. 422)
            if (response.error && response.error.response?.data?.errors) {
                 setErrors(response.error.response.data.errors);
                 setToaster({type:'danger', heading: 'Validation Error', message: 'Please check the required fields.'});
                 setShowToaster(true);
                 return; // Stop
            }
            
            // Handle other generic errors
            if (response.error) {
                 setToaster({type:'danger', heading: 'Error', message: 'Report generation failed.'});
                 setShowToaster(true);
                 return; // Stop
            }

            // --- Success ---
            if (reportFormat === 'pdf') {
                const file = new Blob([response], { type: 'application/pdf' });
                const fileURL = URL.createObjectURL(file);
                setPdfUrl(fileURL);
            } else if (reportFormat === 'spreadsheet' && response.success) {
                const link = document.createElement('a');
                link.href = 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' + response.file;
                link.download = response.filename || 'report.xlsx';
                link.click();
                setToaster({ type: 'success', message: 'Report downloaded successfully.' }); setShowToaster(true);
            }
        });
    };

    // --- Render ---
    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main className="container-fluid">
                <AppBreadCrumb title={`${moduleTitle} Reports`} />
                
                <div className="row">
                    <div className="col-md-4 col-lg-3">
                        <div className="card">
                            <div className="card-header"><h5>Report Options</h5></div>
                            <div className="card-body">
                                {loadingConfig ? (
                                    <div className="text-center"><div className="loader_14"></div></div>
                                ) : (
                                    <form onSubmit={e => e.preventDefault()}>
                                        <div className="mb-3">
                                            <label className="form-label">Select Report</label>
                                            <select className="form-select" value={selectedReport || ''} onChange={e => setSelectedReport(e.target.value)}>
                                                {config && Object.keys(config).map(key => (
                                                    <option key={key} value={key}>
                                                        {capitalizeFirstLetter(key.replace(/-/g, ' '))}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label d-block">Report Type</label>
                                            <div className="btn-group w-100" role="group">
                                                <input type="radio" className="btn-check" name="reportType" id="rt_abstract" value="abstract" checked={reportType === 'abstract'} onChange={() => setReportType('abstract')} disabled={!config?.[selectedReport]?.abstract} />
                                                <label className="btn btn-outline-primary" htmlFor="rt_abstract">Abstract</label>

                                                <input type="radio" className="btn-check" name="reportType" id="rt_detail" value="detail" checked={reportType === 'detail'} onChange={() => setReportType('detail')} disabled={!config?.[selectedReport]?.detail} />
                                                <label className="btn btn-outline-primary" htmlFor="rt_detail">Detailed</label>
                                            </div>
                                        </div>

                                        <hr />
                                        <h6 className="mb-2">Filters</h6>

                                        {hasFilter('date-range') && (
                                            <div className="mb-3">
                                                <label className="form-label">Date Range</label>
                                                <div className="input-group mb-2">
                                                    <span className="input-group-text">From</span>
                                                    <DatePicker selected={filters.from_date} onChange={date => setFilters({...filters, from_date: date})} className="form-control" dateFormat="dd-MM-yyyy" />
                                                </div>
                                                <div className="input-group">
                                                    <span className="input-group-text" style={{minWidth: '58px'}}>To</span>
                                                    <DatePicker selected={filters.to_date} onChange={date => setFilters({...filters, to_date: date})} className="form-control" dateFormat="dd-MM-yyyy" />
                                                </div>
                                            </div>
                                        )}
                                        
                                        {hasFilter('date-only') && (
                                             <div className="mb-3">
                                                <label className="form-label">As On Date</label>
                                                <DatePicker selected={filters.to_date} onChange={date => setFilters({...filters, to_date: date})} className="form-control" dateFormat="dd-MM-yyyy" />
                                            </div>
                                        )}

                                        {hasFilter('party') && (
                                            <div className="mb-3">
                                                <label className="form-label">Party (Optional)</label>
                                                <Select options={partyOptions} isClearable placeholder="All Parties"
                                                    onChange={opt => setFilters({...filters, party_id: opt?.value || null})}
                                                />
                                            </div>
                                        )}
                                        
                                        {hasFilter('product') && (
                                            <div className="mb-3">
                                                <label className="form-label">Product *</label>
                                                <Select options={productOptions} isClearable placeholder="Select a Product"
                                                    onChange={opt => setFilters({...filters, product_id: opt?.value || null})}
                                                />
                                                <span className="text-danger small">{errors.product_id}</span>
                                            </div>
                                        )}

                                        {hasFilter('hoa') && (
                                            <div className="mb-3">
                                                <label className="form-label">Account Head (HOA) *</label>
                                                <Select 
                                                    options={hoaOptions} 
                                                    isClearable 
                                                    placeholder="Select an Account"
                                                    onChange={opt => setFilters({...filters, hoa_id: opt?.value || null})}
                                                />
                                                <span className="text-danger small">{errors.hoa_id}</span>
                                            </div>
                                        )}

                                        <hr />
                                        
                                        <div className="mb-3">
                                            <label className="form-label">Output Format</label>
                                            <div className="d-flex gap-3">
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="rFmt" id="rf_pdf" checked={reportFormat === 'pdf'} onChange={() => setReportFormat('pdf')} />
                                                    <label className="form-check-label" htmlFor="rf_pdf"><i className="ti ti-file-type-pdf text-danger"></i> PDF</label>
                                                </div>
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="rFmt" id="rf_xls" checked={reportFormat === 'spreadsheet'} onChange={() => setReportFormat('spreadsheet')} />
                                                    <label className="form-check-label" htmlFor="rf_xls"><i className="ti ti-file-type-xls text-success"></i> Excel</label>
                                                </div>
                                            </div>
                                        </div>

                                        <button className="btn btn-red w-100" onClick={handleGenerate} disabled={generating || loadingConfig}>
                                            {generating ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Generating...</> : 'Generate Report'}
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-8 col-lg-9">
                        <div className="card h-100">
                            <div className="card-header"><h5>Report Preview</h5></div>
                            <div className="card-body p-0 d-flex align-items-center justify-content-center" style={{minHeight: '500px', background: '#f8f9fa'}}>
                                {generating && <div className="text-center p-5"><div className="loader_14 mb-3"></div><h6 className="text-muted">Generating Report... Please wait.</h6></div>}
                                
                                {!generating && pdfUrl && reportFormat === 'pdf' && (
                                    <iframe src={pdfUrl} title="Report Preview" width="100%" height="800px" style={{border: 'none'}}></iframe>
                                )}

                                {!generating && !pdfUrl && (
                                    <div className="text-center text-muted p-5">
                                        <i className="ti ti-chart-pie fs-1 mb-3 d-block opacity-50"></i>
                                        <p>Select options and click "Generate Report" to view results here.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default GenericReportsPage;